import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Flame, ShieldCheck, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicConsultor() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/consultores/${encodeURIComponent(userId)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status === 404
        ? "Este consultor ainda não tem perfil público (falta bio ou foto)."
        : "Erro ao carregar perfil"));
  }, [userId]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <div className="p-6 border border-slate-200 rounded-xl bg-slate-50 text-slate-600 text-sm" data-testid="consultor-error">
        {err}
      </div>
    </div>
  );

  if (!data) return <div className="max-w-2xl mx-auto px-6 py-16 text-slate-500" data-testid="consultor-loading">Carregando…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="consultor-public-page">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Perfil público · Consultor RAF Direct</div>
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-6 flex items-start gap-6 flex-wrap">
          <img
            src={data.avatar_url}
            alt={data.code}
            className="w-24 h-24 rounded-full object-cover border-2 border-slate-100"
            data-testid="consultor-avatar"
          />
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-bold text-3xl text-[#0B2B62]" data-testid="consultor-code">{data.code}</h1>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-testid="consultor-bio">{data.bio}</p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" /> Consultor RAF verificado
              </span>
              {data.on_call && (
                <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Em plantão agora
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-0 border-t border-slate-100">
          <Stat label="Streak" value={<span className="inline-flex items-center gap-1"><Flame className="w-4 h-4 text-orange-500" /> {data.streak_days}</span>} />
          <Stat label="Dias ativos (60d)" value={data.active_days_60d} />
          <Stat label="Casos concluídos" value={data.cases_concluded} />
        </div>
      </div>

      <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-600 flex items-start gap-2">
        <MessageCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <span>Este perfil é anonimizado (primeiro nome + inicial) para proteger a identidade do consultor. Contato só via canal oficial RAF — nunca peça telefone direto.</span>
      </div>

      <Link to="/ranking" className="inline-block mt-6 text-sm text-[#0B2B62] hover:underline">← Ver ranking completo</Link>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="p-4 text-center border-r last:border-r-0 border-slate-100">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className="font-display font-bold text-2xl text-[#0B2B62] mt-1">{value}</div>
  </div>
);
